// =============================================================================
//  apiDynamicSwipers.js — client for the Dynamic Swiper system
//  (server_elakay/elakay_routes/dynamicSwipers.js).
//
//  Administration (Swiper Manager, Studio, Animation Settings placements,
//  Swiper Access & Limits, My Shop main slider) sends the Bearer token; the
//  server decides per user what is allowed (roles, ownership, quotas); the page configuration read is
//  public and cached per page in memory, with in-flight de-duplication, so
//  a page makes ONE request for all of its swipers and navigating back to it
//  costs nothing.
// =============================================================================

import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { pageCacheKey } from "../dynamicSwipers/dynamicSwiperRegistry";
import { assertRequestFits, prepareUpload } from "../../utils/uploadPolicy";
import { messageForStatus } from "../../utils/uploadResponse";

const getToken = () => isAuthenticated()?.token || "";

const readJson = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

async function apiFetch(path, { method = "GET", body, form, token } = {}) {
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const init = { method, headers };
  if (form) {
    init.body = form; // the browser sets the multipart boundary itself
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  const response = await fetch(`${elakay_API}${path}`, init);
  const data = await readJson(response);
  if (!response.ok) {
    // A proxy answer (nginx 413 page) has no JSON body: readable message.
    const error = new Error(data.error || messageForStatus(response.status, data));
    error.status = response.status;
    error.code = data.code || (response.status === 413 ? "REQUEST_TOO_LARGE" : undefined);
    error.details = data.details;
    throw error;
  }
  return data;
}

// Slide images heavier than their target are re-encoded before sending (all
// slides travel in one request: the proxy ceiling applies to their total).
const prepareSwiperFiles = async (files = {}) => {
  const entries = await Promise.all(Object.entries(files).map(async ([field, file]) => [field, file ? await prepareUpload("swiperImage", file) : file]));
  // All slides travel together: refuse before sending when the total would
  // exceed the proxy / Cloudflare ceiling (GIF and SVG are never compressed).
  assertRequestFits(entries.map(([, file]) => file), "/elakay_api/dynamic-swipers");
  return Object.fromEntries(entries);
};

const pageQuery = (page) => new URLSearchParams(
  page.pageType === "category"
    ? { pageType: "category", categoryId: String(page.categoryId) }
    : { pageType: "main", pageKey: page.pageKey }
).toString();

// Builds the multipart body: the JSON payload + one file per new upload
// (field names image_0..image_2, referenced by each slide's uploadField).
export const buildSwiperFormData = (payload, files = {}) => {
  const form = new FormData();
  form.append("payload", JSON.stringify(payload));
  Object.entries(files).forEach(([field, file]) => {
    if (file) form.append(field, file, file.name || `${field}.png`);
  });
  return form;
};

// ─── Public page configuration (cached) ──────────────────────────────────

const PAGE_CACHE_TTL_MS = 60 * 1000;
const pageCache = new Map();

export const invalidatePublicPageSwipers = () => pageCache.clear();

export const getPublicPageSwipers = (page) => {
  const key = pageCacheKey(page);
  if (!key) return Promise.resolve({ placements: [] });
  const cached = pageCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const promise = apiFetch(`/dynamic-swipers/public/page?${pageQuery(page)}`)
    .then((data) => ({ placements: Array.isArray(data.placements) ? data.placements : [] }))
    .catch(() => {
      // Never let a promotional section break a page: no swipers instead.
      pageCache.delete(key);
      return { placements: [] };
    });
  pageCache.set(key, { promise, expiresAt: Date.now() + PAGE_CACHE_TTL_MS });
  return promise;
};

// ─── Family administration ───────────────────────────────────────────────

const mutate = async (request) => {
  const result = await request;
  invalidatePublicPageSwipers();
  // eslint-disable-next-line no-use-before-define
  swiperCache.clear();
  return result;
};

export const getDynamicSwiperRegistry = (token) =>
  apiFetch("/dynamic-swipers/registry", { token: token || getToken() });

export const listDynamicSwipers = (params = {}, token) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, String(value));
  });
  const suffix = query.toString() ? `?${query}` : "";
  return apiFetch(`/dynamic-swipers${suffix}`, { token: token || getToken() });
};

export const getDynamicSwiper = (swiperId, token) =>
  apiFetch(`/dynamic-swipers/${encodeURIComponent(swiperId)}`, { token: token || getToken() });

export const createDynamicSwiper = async (payload, files, token) => mutate(
  apiFetch("/dynamic-swipers", { method: "POST", form: buildSwiperFormData(payload, await prepareSwiperFiles(files)), token: token || getToken() })
);

export const updateDynamicSwiper = async (swiperId, payload, files, token) => mutate(
  apiFetch(`/dynamic-swipers/${encodeURIComponent(swiperId)}`, {
    method: "PUT", form: buildSwiperFormData(payload, await prepareSwiperFiles(files)), token: token || getToken(),
  })
);

export const setDynamicSwiperStatus = (swiperId, status, token) => mutate(
  apiFetch(`/dynamic-swipers/${encodeURIComponent(swiperId)}/status`, { method: "PATCH", body: { status }, token: token || getToken() })
);

export const duplicateDynamicSwiper = (swiperId, token) => mutate(
  apiFetch(`/dynamic-swipers/${encodeURIComponent(swiperId)}/duplicate`, { method: "POST", token: token || getToken() })
);

export const deleteDynamicSwiper = (swiperId, token) => mutate(
  apiFetch(`/dynamic-swipers/${encodeURIComponent(swiperId)}`, { method: "DELETE", token: token || getToken() })
);

export const getSwiperPageLayout = (page, token) =>
  apiFetch(`/dynamic-swipers/placements/page?${pageQuery(page)}`, { token: token || getToken() });

// Replaces the page's whole layout in one atomic save. `placements` is the
// ordered list [{ swiperId, slotKey, active }]; the server assigns positions
// in that order and answers 409 LAYOUT_CONFLICT if `expectedRevision` is stale.
export const saveSwiperPageLayout = (page, placements, expectedRevision, token) => mutate(
  apiFetch("/dynamic-swipers/placements/page", {
    method: "PUT", body: { page, placements, expectedRevision }, token: token || getToken(),
  })
);

// ─── Swiper Studio ───────────────────────────────────────────────────────

// Uploads ONE image / GIF / video used by a canvas slide. Stored immediately
// by the server; the returned `media` ({ kind, url, width, height, mimeType,
// byteLength }) is then only referenced by the swiper save.
export const uploadDynamicSwiperMedia = async (file, token) => {
  const prepared = await prepareUpload(String(file?.type || "").startsWith("video/") ? "swiperVideo" : "swiperImage", file);
  const form = new FormData();
  form.append("file", prepared, prepared.name || file.name || "media");
  return apiFetch("/dynamic-swipers/media", { method: "POST", form, token: token || getToken() });
};

// Studio media library (Family): images / GIFs / videos already used by
// swipers, reusable without uploading them again. kind: "image" | "video".
export const listStudioMediaLibrary = (kind, token) =>
  apiFetch(`/dynamic-swipers/media-library${kind ? `?kind=${encodeURIComponent(kind)}` : ""}`, { token: token || getToken() });

// One swiper by id, for builders that embed a swiper directly (Page Builder,
// Shop Builder, Tutorials). Public, cached like the page configuration;
// resolves to null (never throws) when the swiper is inactive or gone.
const swiperCache = new Map();
export const invalidatePublicSwiper = (swiperId) => {
  if (!swiperId) { swiperCache.clear(); return; }
  [...swiperCache.keys()].filter((key) => key.startsWith(`${swiperId}:`)).forEach((key) => swiperCache.delete(key));
};

// `ownerId`: the user whose page / shop displays it. A Family swiper shown
// in another role's shop is hidden there once Family stops sharing it (or
// that role loses access to shared swipers) -- the server decides.
export const getPublicSwiper = (swiperId, { ownerId = null } = {}) => {
  const id = String(swiperId || "");
  if (!/^[0-9a-fA-F]{24}$/.test(id)) return Promise.resolve(null);
  const owner = /^[0-9a-fA-F]{24}$/.test(String(ownerId || "")) ? String(ownerId) : "";
  const key = `${id}:${owner}`;
  const cached = swiperCache.get(key);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const promise = apiFetch(`/dynamic-swipers/public/swiper/${encodeURIComponent(id)}${owner ? `?owner=${owner}` : ""}`)
    .then((data) => data.swiper || null)
    .catch(() => null);
  swiperCache.set(key, { promise, expiresAt: Date.now() + PAGE_CACHE_TTL_MS });
  return promise;
};

// Swiper Library for the content builders (Page / Shop Builder, Tutorials,
// Email -- context "pageBuilder") and My Shop Pub (context "shop"): Family
// sees every active swiper; others their own + the Family swipers shared
// with their role, if their role may place swipers there. Signed-in only.
export const listSwiperLibrary = (params = {}, token) => {
  const query = new URLSearchParams();
  if (params.search) query.set("search", String(params.search));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.context) query.set("context", String(params.context));
  const suffix = query.toString() ? `?${query}` : "";
  return apiFetch(`/dynamic-swipers/library${suffix}`, { token: token || getToken() });
};

// ─── Swiper Access & Limits (Animation Settings) ──────────────────────────

// The signed-in user's own rights and quotas:
// { roleKey, isFamily, permissions, limits: { maxSwipers, maxSlides }, owned, canUseManager }.
// Cached for the session; refreshed after any change that affects it.
let accessCache = null;
export const invalidateSwiperAccess = () => { accessCache = null; };
export const getSwiperAccess = ({ refresh = false, token, roleKey = "" } = {}) => {
  const authToken = token || getToken();
  if (!authToken) return Promise.resolve(null);
  const accessKey = `${authToken}:${roleKey || "default"}`;
  if (!refresh && accessCache && accessCache.key === accessKey) return accessCache.promise;
  const promise = apiFetch("/dynamic-swipers/access/me", { token: authToken })
    .then((data) => data.access || null)
    .catch(() => {
      accessCache = null;
      return null;
    });
  accessCache = { key: accessKey, promise };
  return promise;
};

export const getSwiperAccessSettings = (token) =>
  apiFetch("/dynamic-swipers/access/settings", { token: token || getToken() });

export const saveSwiperAccessSettings = async (roles, token) => {
  const result = await apiFetch("/dynamic-swipers/access/settings", { method: "PUT", body: { roles }, token: token || getToken() });
  invalidateSwiperAccess();
  swiperCache.clear();
  return result;
};

// ─── My Shop Pub: Main Shop Slider Source ─────────────────────────────────

export const getShopHero = (token) => apiFetch("/dynamic-swipers/shop-hero", { token: token || getToken() });

// source "gallery" | "swiper"; swiperId null with "gallery" = remove the swiper.
export const setShopHero = (source, swiperId, token) => mutate(
  apiFetch("/dynamic-swipers/shop-hero", {
    method: "PUT",
    body: { source, ...(swiperId !== undefined ? { swiperId } : {}) },
    token: token || getToken(),
  })
);
